import React, { useState, useEffect } from 'react';

const AboutUsPage = () => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const aboutSection = document.querySelector('.about-us-container');
    if (aboutSection) {
      observer.observe(aboutSection);
    }

    return () => {
      if (aboutSection) {
        observer.unobserve(aboutSection);
      }
    };
  }, []);

  const reasons = [
    {
      icon: '💬',
      title: 'Quick response',
      description: 'We can log in to your PC or server remotely and resolve many issues immediately without the wait for a technician to travel to your location.',
    },
    {
      icon: '🏆',
      title: 'Experienced',
      description: 'In more than 30 years of IT outsourcing, we have gained experience in a wide spectrum of technologies, industries, and application types.',
    },
    {
      icon: '🗣️',
      title: 'No geek speak',
      description: 'You deserve to have your questions answered in plain English. Our technicians will clearly explain what is happening so you understand.',
    },
    {
      icon: '🚩',
      title: 'Business savvy',
      description: 'We design, evaluate and justify technology solutions from a thorough understanding of the business benefit for your company.',
    },
    {
      icon: '💻',
      title: 'One Stop Shop',
      description: 'We handle all aspects of your IT infrastructure including hardware, software management and any other related technology needs.',
    },
    {
      icon: '✅',
      title: '100% Satisfaction Guarantee',
      description: 'We want you to be completely satisfied with our services. We will do whatever it takes to make you happy. No hassles, no problems.',
    },
  ];

  // Inline style for the main container, matching the new light theme
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '3rem 0', // Reduced top padding
    background: '#f0f4f8',
    position: 'relative',
    fontFamily: 'sans-serif',
    color: '#334155',
    overflow: 'hidden',
  };

  // Main content wrapper style
  const contentWrapperStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    position: 'relative',
    zIndex: 2,
    padding: '3rem 2rem', // Reduced top padding
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
  };

  // Header section style
  const headerSectionStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '3rem',
    flexWrap: 'wrap',
  };

  // Header text styles
  const headerTextStyle = { flex: '1' };
  const subHeadingStyle = {
    color: '#d4b68f',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '1px',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
  };

  const mainHeadingStyle = {
    color: '#1f2937',
    backgroundClip: 'text',
    backgroundImage: 'none',
    fontSize: '2.5rem',
    fontWeight: '700',
    lineHeight: '1.2',
    margin: '0',
    fontFamily: 'serif',
  };

  const headerDescriptionStyle = {
    flex: '1',
    paddingLeft: '2rem',
    display: 'flex',
    alignItems: 'center',
    color: '#475569',
    fontSize: '1rem',
    lineHeight: '1.6',
  };

  // Reasons grid style
  const reasonsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    padding: '2rem',
    borderRadius: '10px',
    backgroundColor: '#f1f5f9',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  };

  // Individual reason card styles
  const reasonCardStyle = (index) => ({
    display: 'flex',
    flexDirection: 'column',
    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(30px)',
    transitionDelay: `${index * 0.15}s`,
    backgroundColor: '#ffffff',
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  });

  const iconContainerStyle = {
    fontSize: '1.8rem',
    backgroundColor: '#f1f5f9',
    borderRadius: '8px',
    padding: '8px',
    marginRight: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const reasonTitleStyle = {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#d4b68f',
    margin: '0',
  };

  const reasonDescriptionStyle = {
    fontSize: '0.9rem',
    color: '#475569',
    lineHeight: '1.5',
    marginLeft: '3rem',
  };

  return (
    <div className="about-us-container" style={containerStyle}>
      <div className="about-content-wrapper" style={contentWrapperStyle}>
        <div className="about-header-section" style={headerSectionStyle}>
          <div className="about-header-text" style={headerTextStyle}>
            <p style={subHeadingStyle}>WHY CHOOSE US</p>
            <h1 style={mainHeadingStyle}>
              6 REASONS TO PARTNER WITH GT
            </h1>
          </div>
          <div className="about-header-description" style={headerDescriptionStyle}>
            <p style={{ margin: 0 }}>
              Here are 6 reasons why you should choose us to build your infrastructure, support your people and systems, as well as advise you on projects that will reduce your risk.
            </p>
          </div>
        </div>
        <div className="reasons-grid" style={reasonsGridStyle}>
          {reasons.map((reason, index) => (
            <div key={index} style={reasonCardStyle(index)}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={iconContainerStyle}>
                  <span style={{ color: '#1f2937' }}>{reason.icon}</span>
                </div>
                <h3 style={reasonTitleStyle}>{reason.title}</h3>
              </div>
              <p style={reasonDescriptionStyle}>{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 992px) {
          .about-header-section {
            flex-direction: column;
            align-items: flex-start;
          }
          .about-header-description {
            padding-left: 0 !important;
            margin-top: 1.5rem;
          }
        }
        @media (max-width: 768px) {
          .about-content-wrapper {
            padding: 2rem 1rem !important;
          }
          .reasons-grid {
            padding: 1rem !important;
            gap: 1.5rem !important;
          }
          .reasons-grid div {
            padding: 1rem !important;
          }
        }
        @media (max-width: 480px) {
          .about-header-text h1 {
            font-size: 1.8rem !important;
          }
          .about-header-text p {
             font-size: 0.8rem !important;
          }
          .about-header-description p {
            font-size: 0.8rem !important;
          }
          .about-us-container {
             padding-bottom: 2rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutUsPage;